import React from "react";
import { Avatar, Box, Tooltip, Typography, useTheme } from "@mui/material";
import { faAngry, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SpecialDay from "./SpecialDay";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../context/AuthenticationTokenContext";
import QRCode from "react-qr-code";
import { faMessages } from "@fortawesome/pro-solid-svg-icons";
import * as api from "../../services/api";
import { useSnackbarMessage } from "../Snackbar.jsx";
import ProblematicTreatment from "./ProblematicTreatment";
import VirtualCareTag from "../VirtualCareTag.jsx";
import { useClientProblem } from "./hooks/useClientProblem.jsx";
import * as f from "../../utils/formatter";

/**
 * @typedef FileExtension
 * @property {() => void} onload
 *
 * @typedef {import('../../services/api').File & FileExtension} File
 *
 * @returns {File}
 */
function useFile(file) {
  const [state, setState] = React.useState({
    loading: file != null,
    file: null,
  });
  React.useEffect(() => {
    (async function () {
      if (file == null) {
        return;
      }

      const f = await api.file(file);
      setState({ ...f, onload: () => URL.revokeObjectURL(f.url) });
    })();
  }, [file]);

  return state;
}

function ClientAvatar({
  client,
  t,
  additionalContent = null,
  nameDayNames,
  displayMinimalInfo = false,
  canSeeClientProblem = false,
}) {
  const profile_photo = useFile(client.profile_photo);
  const theme = useTheme();
  const roles = useRoles();
  const problem = useClientProblem(client, roles);
  const canSeeSpecialDay = isGrantedAny(
    roles,
    new Set([
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );

  const canSeeQrCode = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const displaySnackbarMessage = useSnackbarMessage();
  const canSeeProblemTooltip = isGrantedAny(
    roles,
    new Set([role.Dental_Assistant, role.Floor_Manager]),
  );

  const canSeeAngryFace = canSeeClientProblem;

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      {!displayMinimalInfo && (
        <Avatar
          sx={{
            width: 88,
            height: 88,
            mr: 2,
            backgroundColor: "grey.200",
            border: "5px solid white",
          }}
          src={profile_photo.url}
          slotProps={{ img: { onLoad: profile_photo.onload } }}
        >
          <FontAwesomeIcon icon={faUser} color={theme.palette.grey[500]} />
        </Avatar>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <VirtualCareTag client={client} />
        <Typography variant="h1">
          {client ? f.fullName(client) : t("client.notFound")}
        </Typography>
        {!displayMinimalInfo && <ProblematicTreatment client={client} />}
        {!displayMinimalInfo && canSeeSpecialDay && (
          <SpecialDay
            client={client}
            nameDayNames={nameDayNames}
            iconProps={{
              size: "xl",
            }}
          />
        )}
        {problem && canSeeAngryFace && (
          <Tooltip title={problem}>
            <FontAwesomeIcon icon={faAngry} color={theme.palette.error.main} />
          </Tooltip>
        )}
        {problem &&
          canSeeProblemTooltip &&
          !canSeeAngryFace &&
          !displayMinimalInfo && (
            <FontAwesomeIcon
              icon={faMessages}
              color={theme.palette.primary.main}
              onClick={() =>
                displaySnackbarMessage({
                  content: problem,
                  severity: "warning",
                  anchorOrigin: { vertical: "bottom", horizontal: "right" },
                })
              }
            />
          )}
        {additionalContent}
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {client.labs_id && canSeeQrCode && (
          <Box>
            <QRCode
              title={`ID klienta - ${client.labs_id}`}
              size={88}
              style={{ width: "100%", height: "100%" }}
              value={client.labs_id.toString()}
              bgColor={theme.palette.background.default}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ClientAvatar;
